import { useEffect, useRef, useState } from "react";
import { ESIInclude } from "../__shared__/ESIInclude/ESIInclude";
import styles from "./Breadcrumbs.module.scss";
import classNames from "classnames/bind";

const cx = classNames.bind(styles);
type Props = {
  /**
   * A string representing the breadcrumb path.
   */
  breadcrumbPath: string;
  /**
   * A boolean on whether this is beauty simplification
   */
  beautySimplification: boolean;
  /**
   * boolean if the config prop and breadcrumb position match
   */
  isBreadcrumbsMatchPositionTwo?: boolean;
};

export const BreadcrumbsUI = ({
  breadcrumbPath,
  beautySimplification,
  isBreadcrumbsMatchPositionTwo = false,
}: Props) => {
  const breadcrumbsRef = useRef<HTMLDivElement>(null);
  const [hasHighlightedReviewContent, setHasHighlightedReviewContent] =
    useState(false);

  useEffect(() => {
    if (!isBreadcrumbsMatchPositionTwo) {
      setHasHighlightedReviewContent(false);
      return;
    }

    let componentObserver: MutationObserver | null = null;
    let rootObserver: MutationObserver | null = null;
    let observedComponent: Element | null = null;
    let observedShadowRoot: ShadowRoot | null = null;

    const checkForHighlightedReview = (reviewComponent: Element) => {
      const lightDomMatch = reviewComponent.querySelector("#HighlightedReview");
      const shadowDomMatch =
        reviewComponent.shadowRoot?.querySelector("#HighlightedReview");

      setHasHighlightedReviewContent(Boolean(lightDomMatch || shadowDomMatch));
    };

    const observeReviewComponent = (reviewComponent: Element) => {
      if (observedComponent === reviewComponent) {
        if (
          reviewComponent.shadowRoot &&
          observedShadowRoot !== reviewComponent.shadowRoot
        ) {
          componentObserver?.observe(reviewComponent.shadowRoot, {
            childList: true,
            subtree: true,
            attributes: true,
          });
          observedShadowRoot = reviewComponent.shadowRoot;
        }
        checkForHighlightedReview(reviewComponent);
        return;
      }

      componentObserver?.disconnect();
      observedShadowRoot = null;
      componentObserver = new MutationObserver(() => {
        if (
          reviewComponent.shadowRoot &&
          observedShadowRoot !== reviewComponent.shadowRoot
        ) {
          componentObserver?.observe(reviewComponent.shadowRoot, {
            childList: true,
            subtree: true,
            attributes: true,
          });
          observedShadowRoot = reviewComponent.shadowRoot;
        }
        checkForHighlightedReview(reviewComponent);
      });

      componentObserver.observe(reviewComponent, {
        childList: true,
        subtree: true,
        attributes: true,
      });

      if (reviewComponent.shadowRoot) {
        componentObserver.observe(reviewComponent.shadowRoot, {
          childList: true,
          subtree: true,
          attributes: true,
        });
        observedShadowRoot = reviewComponent.shadowRoot;
      }

      observedComponent = reviewComponent;
      checkForHighlightedReview(reviewComponent);
    };

    const findAndObserveComponent = () => {
      const reviewContainer = document.getElementById(
        "standardPDPHighlightedReview",
      );
      const reviewComponent = reviewContainer?.querySelector(
        "jl-reviews-highlight-review",
      );

      if (!(reviewComponent instanceof HTMLElement)) {
        componentObserver?.disconnect();
        setHasHighlightedReviewContent(false);
        observedComponent = null;
        observedShadowRoot = null;
        return;
      }

      observeReviewComponent(reviewComponent);
    };

    rootObserver = new MutationObserver(findAndObserveComponent);
    rootObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    findAndObserveComponent();

    return () => {
      componentObserver?.disconnect();
      rootObserver?.disconnect();
    };
  }, [isBreadcrumbsMatchPositionTwo]);

  return (
    <div
      ref={breadcrumbsRef}
      data-testid="breadcrumbs"
      className={cx("esi-wrapper", {
        beautySimplification,
        breadcrumbsPositionTwo: isBreadcrumbsMatchPositionTwo,
        highlightedReviewSpacing:
          isBreadcrumbsMatchPositionTwo && hasHighlightedReviewContent,
      })}
      data-component="Breadcrumbs"
    >
      <ESIInclude src={breadcrumbPath} id={"breadcrumbs"} />
    </div>
  );
};
